import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { requestJson, buildApiUrl } from "../../dataProvider/requestJson";
import {
  MAPS_RESOLVE,
  MAPS_LOGO_SEARCH,
  MAPS_LOGO_IMPORT,
} from "../../constants/endpoints";
import { ALERT_TEXT } from "../../constants/titles";

const initialState = {
  resolving: false,
  importingLogo: false,
  searchingLogo: false,
  result: null,
  error: null,
};

const buildUrl = buildApiUrl;

// These endpoints are system-admin only and the backend's 403 wording is generic, so
// substitute a message that says what to do about it.
const MAPS_STATUS_MESSAGES = { 403: ALERT_TEXT.FORBIDDEN_MAPS_AUTOFILL };

/**
 * Resolve a Google Maps link into team and/or venue drafts.
 * Persists nothing — creation still goes through createTeam / createVenue.
 */
export const resolveMapsLink = createAsyncThunk(
  "mapsAutofill/resolve",
  async ({ link, type, includeVenue = false }, { rejectWithValue }) => {
    try {
      return await requestJson(
        buildUrl(MAPS_RESOLVE),
        {
          method: "POST",
          body: JSON.stringify({ link, type, includeVenue }),
        },
        MAPS_STATUS_MESSAGES,
      );
    } catch (error) {
      return rejectWithValue(error?.message || ALERT_TEXT.MAPS_RESOLVE_FAILED);
    }
  },
);

/**
 * Re-run just the logo lookup, e.g. after the admin corrects the team name.
 * Pass `website` whenever it is known — the team's own site is checked first and is
 * far more accurate than the image-search fallback.
 */
export const searchTeamLogo = createAsyncThunk(
  "mapsAutofill/searchLogo",
  async ({ name, city, state, website }, { rejectWithValue }) => {
    try {
      return await requestJson(
        buildUrl(MAPS_LOGO_SEARCH({ name, city, state, website })),
        undefined,
        MAPS_STATUS_MESSAGES,
      );
    } catch (error) {
      return rejectWithValue(error?.message || "Logo search failed");
    }
  },
);

/**
 * Download the chosen candidate. Call ONLY when the admin keeps the proposed logo.
 * Returns { logoUrl (base64 data URI), logoChanged, width, height, squareness }.
 */
export const importTeamLogo = createAsyncThunk(
  "mapsAutofill/importLogo",
  async ({ imageUrl }, { rejectWithValue }) => {
    try {
      return await requestJson(
        buildUrl(MAPS_LOGO_IMPORT),
        {
          method: "POST",
          body: JSON.stringify({ imageUrl }),
        },
        MAPS_STATUS_MESSAGES,
      );
    } catch (error) {
      return rejectWithValue(
        error?.message || ALERT_TEXT.MAPS_LOGO_IMPORT_FAILED,
      );
    }
  },
);

export const mapsAutofillSlice = createSlice({
  name: "mapsAutofill",
  initialState,
  reducers: {
    clearMapsAutofill: () => ({ ...initialState }),
    clearMapsAutofillError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(resolveMapsLink.pending, (state) => {
        state.resolving = true;
        state.error = null;
      })
      .addCase(resolveMapsLink.fulfilled, (state, action) => {
        state.resolving = false;
        state.result = action.payload;
      })
      .addCase(resolveMapsLink.rejected, (state, action) => {
        state.resolving = false;
        state.error = action.payload || ALERT_TEXT.MAPS_RESOLVE_FAILED;
      })

      .addCase(searchTeamLogo.pending, (state) => {
        state.searchingLogo = true;
      })
      .addCase(searchTeamLogo.fulfilled, (state) => {
        state.searchingLogo = false;
      })
      .addCase(searchTeamLogo.rejected, (state) => {
        state.searchingLogo = false;
      })

      .addCase(importTeamLogo.pending, (state) => {
        state.importingLogo = true;
      })
      .addCase(importTeamLogo.fulfilled, (state) => {
        state.importingLogo = false;
      })
      .addCase(importTeamLogo.rejected, (state) => {
        state.importingLogo = false;
      }),
});

export const { clearMapsAutofill, clearMapsAutofillError } =
  mapsAutofillSlice.actions;

export default mapsAutofillSlice.reducer;
